import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../components/ui";
import { adminLogo } from "../../assets/img/image";
import { useAppMutation } from "../../api/fetchClient";
import { SUPER_ADMIN_VERIFY_OTP_URL, SUPER_ADMIN_RESEND_OTP_URL } from "../../api/url";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../../utils/keys"; 
import { strings } from "../../strings/strings";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

const VerifyOtpUI = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const otpRefs = useRef([]);
  const verifyMutation = useAppMutation();
  const resendMutation = useAppMutation();
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const { userId, role, otpReference } = location.state || {};
  // eslint-disable-next-line no-unused-vars
  const [otpRef, setOtpRef] = useState(otpReference || "");
  useEffect(() => {
    if (!userId || !role) {

      navigate("/login", { replace: true });
    }
  }, [userId, role,otpReference, navigate]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);
useEffect(() => {

  const t = setTimeout(() => focusIndex(0), 0);
  return () => clearTimeout(t);
}, []);

  const otpValue = useMemo(() => otp.join(""), [otp]);
  const isOtpComplete = otp.every((digit) => digit !== "");

  const focusIndex = (i) => {
    const el = otpRefs.current?.[i];
    if (el) el.focus();
  };

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);

    setOtp((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    if (digit && index < OTP_LENGTH - 1) {
      focusIndex(index + 1);
    }
  };

const handleKeyDown = (index, e) => {
  if (e.key === "Enter") {
    e.preventDefault();
    if (isOtpComplete && !verifyMutation.isPending) {
      handleVerify();
    }
    return;
  }

  if (e.key === "Backspace") {
    if (!otp[index] && index > 0) {
      setOtp((prev) => {
        const next = [...prev];
        next[index - 1] = "";
        return next;
      });
      focusIndex(index - 1);
    } else {
      setOtp((prev) => {
        const next = [...prev];
        next[index] = "";
        return next;
      });
    }
  }

  if (e.key === "ArrowLeft" && index > 0) focusIndex(index - 1);
  if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) focusIndex(index + 1);
};

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text") || "";
    const digits = text.replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!digits) return;

    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    digits.split("").forEach((d, i) => (next[i] = d));
    setOtp(next);

    const lastIndex = Math.min(digits.length, OTP_LENGTH) - 1;
    focusIndex(lastIndex >= 0 ? lastIndex : 0);
  };

  const handleVerify = async () => {
    if (!isOtpComplete) return;

    try {
      const res = await verifyMutation.mutateAsync({
        url: SUPER_ADMIN_VERIFY_OTP_URL,
        method: "POST",
        data: { 
          otp: otpValue,
          role: role,
          userId: userId,
           otpReference:otpReference, 
        },
      });
const newRef = res?.results?.otpReference || res?.otpReference;
if (newRef) setOtpRef(newRef);
      toast.success(res?.message );

      const user = res.results || {};
      const accessToken = res.accessToken || user.accessToken;
      const refreshToken = res.refreshToken || user.refreshToken;
      const username = res.username || user.username || "";
      const email = res.email || user.email || "";
      const country = res.country || user.country || "";
      const userRole = res.role || user.role;

      if (accessToken) localStorage.setItem(ACCESS_TOKEN, accessToken);
      if (refreshToken) localStorage.setItem(REFRESH_TOKEN, refreshToken);

      let normalizedRole = userRole;
      if (userRole === "super_admin") {
        normalizedRole = "Super Admin";
      } else if (userRole === "country_admin") {
        normalizedRole = "Country Admin";
      }
      localStorage.setItem("role", normalizedRole);

      localStorage.setItem(
        strings.adminData,
        JSON.stringify({
          username: username,
          email: email,
          country: country,
        })
      );
      localStorage.setItem("otp_verified", "true");

      if (userRole === "super_admin") {
        navigate("/superadmin/country-admin", { replace: true });
      } else if (userRole === "country_admin") {
        navigate("/country", { replace: true });
      }
    } catch (err) {
      toast.error(err?.message);
    }
  };

  const handleResend = async () => {
    if (secondsLeft > 0) return;

    try {
      const res = await resendMutation.mutateAsync({
        url: SUPER_ADMIN_RESEND_OTP_URL,
        method: "POST",
        data: { 
          role: role,
          userId: userId
        },
      });

      toast.success(res?.message );
      setSecondsLeft(RESEND_SECONDS);
      setOtp(Array(OTP_LENGTH).fill(""));
      focusIndex(0);
    } catch (err) {
      toast.error(err?.message );
    }
  };

  const isVerifyLoading = verifyMutation.isPending;
  const isResendLoading = resendMutation.isPending;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8">
        <div className="flex justify-center">
          <div className="w-20 h-20 flex items-center justify-center">
            <img src={adminLogo} alt="" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-secondary text-center mb-2">{strings.verifyOtp}</h2>

        <p className="text-center text-secondary mb-6">{strings.enterDigitOtp}</p>

        <div className="flex justify-center text-secondary gap-3 mb-6" onPaste={handlePaste}>
          {Array.from({ length: OTP_LENGTH }).map((_, index) => (
            <input
              key={index}
              ref={(el) => (otpRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              autoComplete="off"
              value={otp[index]}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-12 text-center text-xl font-bold border rounded-lg
                         bg-gray-50
                         focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label={`otp-${index + 1}`}
            />
          ))}
        </div>

        <Button
          onClick={handleVerify}
          disabled={!isOtpComplete || isVerifyLoading}
          isLoading={isVerifyLoading}
          className={`w-full bg-primary text-white py-3 rounded-xl font-semibold ${
            !isOtpComplete || isVerifyLoading ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {strings.veryfingOtp}
        </Button>

        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-secondary">
          <span>{strings.didNotReceiveCode}</span>
          <button
            type="button"
            onClick={handleResend}
            disabled={secondsLeft > 0 || isResendLoading}
            className={`font-semibold ${
              secondsLeft > 0 || isResendLoading ? "opacity-50 cursor-not-allowed" : "text-primary"
            }`}
          >
            {isResendLoading
              ? strings.resending
              : secondsLeft > 0
                ? `${strings.resendIn} ${secondsLeft}${strings.second}`
                : strings.resendOtp}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtpUI;
